'use client';

import React, { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { useRouter, useSearchParams, usePathname } from 'next/navigation';
import { useMedia, usePrevious } from 'react-use';
import { Controller, useForm, useWatch } from 'react-hook-form';

import { LottieRefCurrentProps } from 'lottie-react';

import { CloseIcon, MagicSearchIcon, MagicSearchStarsIcon, SearchIcon } from 'components/icons/v2';
import { Button } from 'components/ui/button';
import { Dialog, DialogContent, DialogOverlay, DialogPortal } from 'components/ui/dialog';
import { Input } from 'components/ui/input';
import { Sheet, SheetContent } from 'components/ui/sheet';

import { MEMBER_ROUTES } from 'utils/routes';

import { useAuth } from 'contexts/auth';
import MagicSearchContext from '../../../_context/MagicSearchContext';
import LinkedinContext from '../../../_context/LinkedinContext';

import { useMemberTracking } from 'app/_tracking/events';

import magicSearchLoadingAnimation from 'app/(authenticated)/(member)/_components/animation/magicSearchLoader.json';

const Lottie = dynamic(() => import('lottie-react'), { ssr: false });

interface ISearch {
  prompt: string;
}

export const MagicSearch: FC = () => {
  const params = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  const [isLoading, setLoading] = useState<boolean>(false);

  const {
    control,
    handleSubmit,
    setValue
  } = useForm<ISearch>({
    mode: 'all',
    values: {
      prompt: params?.get('search') || ''
    }
  });

  const searchWatch = useWatch({
    control,
    name: 'prompt'
  });

  const [open, setOpen] = useState<boolean>(false);

  const { trackMagicSearch } = useMemberTracking();

  const { setLinkedinModalState } = useContext(LinkedinContext);
  const { suggestions } = useContext(MagicSearchContext);
  const { currentUser } = useAuth();

  const isMobile = useMedia('(max-width: 768px)', false);

  const lottieMagicSearchButtonRef = useRef<LottieRefCurrentProps>(null);

  const activeSearch = useMemo(() => {
    return params?.get('search');
  }, [params]);

  const isSearchActive = useMemo(() => (activeSearch ?? '').length > 0, [activeSearch]);

  const prevSearchValue = usePrevious(activeSearch);

  const isDiscoverPage = useMemo(() => pathname === MEMBER_ROUTES.DISCOVERY.getPath(), [pathname]);

  const searchPlaceholder = useMemo(() => 
    isDiscoverPage ? "Spaces Search" : "Members AI search",
  [isDiscoverPage]);

  const handleSelectSuggestion = useCallback((suggestion: string) => {
    trackMagicSearch('suggestion_selected');
    setValue('prompt', suggestion);
  }, [setValue, trackMagicSearch]);

  const onSubmit = useCallback((data: ISearch) => {
    if (data.prompt !== prevSearchValue) {
      setLoading(() => true);
    }

    trackMagicSearch('submitted');
    setOpen(() => false);
    
    const searchPath = isDiscoverPage 
      ? `${MEMBER_ROUTES.DISCOVERY.getPath()}?search=${encodeURIComponent(data.prompt)}`
      : `${MEMBER_ROUTES.MEMBERS.getPath()}?search=${encodeURIComponent(data.prompt)}`;
      
    router.push(searchPath);
  }, [prevSearchValue, router, trackMagicSearch, isDiscoverPage]);

  useEffect(() => {
    if (!activeSearch || activeSearch !== prevSearchValue) {
      setLoading(() => false);
    }
  }, [activeSearch, prevSearchValue]);

  const handleOpenModal = useCallback(() => {
    trackMagicSearch('opened');
    setOpen(prevState => !prevState);
  }, [trackMagicSearch]);

  const DialogRoot = isMobile ? Sheet : Dialog;
  const Content = isMobile ? SheetContent : DialogContent;

  return (
    <>
      <div className="flex gap-[10px]">
        {!activeSearch && !isLoading && (
          <div onClick={(event) => {
            event.preventDefault();
            if (isDiscoverPage) {
              // For Spaces search, don't open dialog, keep focus on the input
              return;
            }
            handleOpenModal();
          }} className={'rounded-full bg-magicGradient p-[2px]'}>
            <Input
              placeholder={searchPlaceholder}
              leftIcon={<SearchIcon height={20} width={20} className="text-gray-600 ml-2" />}
              rightIcon={(
                <Button variant={"nakedGrayLink"} onClick={handleSubmit(onSubmit)} className={'!p-0 !h-auto !border-0'}>
                  <div className='w-8 h-8 rounded-full bg-brand-300 flex items-center justify-center text-brand-500'>
                    <MagicSearchStarsIcon  />
                  </div>
                </Button>
              )}
              className={`placeholder:text-[14px] placeholder:text-gray-500 font-normal bg-white w-[300px] sm-1/2:w-auto ${isDiscoverPage ? '' : 'cursor-pointer'}`}
              containerClassName={`bg-white !rounded-full border-0 ${isDiscoverPage ? '' : 'cursor-pointer'} !p-[5px]`}
              onChange={(e) => {
                if (isDiscoverPage) {
                  setValue('prompt', e.target.value);
                }
              }}
              onKeyDown={(e) => {
                if (isDiscoverPage && e.key === 'Enter') {
                  handleSubmit(onSubmit)();
                }
              }}
            />
          </div>
        )}
        {(activeSearch || isLoading) && (
          <Button
            asChild={!isSearchActive && !isLoading}
            variant="primaryBrand"
            className={`gap-3 p-0 md:!p-0 rounded-full overflow-hidden border-0 ${isSearchActive || isLoading ? 'p-0 w-9 h-9' : '!bg-transparent'}`}
            onClick={() => {
              if (isDiscoverPage) return;
              !currentUser?.profileStatus ? setLinkedinModalState(() => 'WELCOME') : handleOpenModal();
            }}
            onMouseEnter={() => lottieMagicSearchButtonRef.current?.play()}
            onMouseLeave={() => lottieMagicSearchButtonRef.current?.goToAndStop(0)}
          >
            <>
              {isLoading ? (
                <Lottie
                  animationData={magicSearchLoadingAnimation}
                  loop={false}
                />
              ) : (
                <MagicSearchStarsIcon className="text-white" />
              )}
            </>
          </Button>
        )}
        {(isSearchActive || isLoading) && (
          <div
            className="flex items-center gap-[10px] py-[6px] px-3 bg-white rounded-full text-small cursor-pointer font-semibold"
            onClick={() => {
              if (!isDiscoverPage) {
                trackMagicSearch('opened');
                setOpen(() => true);
              }
            }}
          >
            <p className="max-w-[160px] text-ellipsis overflow-hidden whitespace-nowrap">
              {activeSearch || searchWatch}
            </p>
            <Button
              size="x-small"
              className="!p-0"
              variant="nakedBrandLink"
              onClick={(event) => {
                event.stopPropagation();
                router.push(isDiscoverPage ? MEMBER_ROUTES.DISCOVERY.getPath() : MEMBER_ROUTES.MEMBERS.getPath());
              }}
            >
              <CloseIcon height={18} width={18} />
            </Button>
          </div>
        )}
      </div>
      <DialogRoot open={open} onOpenChange={open1 => {
        trackMagicSearch('closed');
        setOpen(() => open1);
      }}>
        <DialogPortal>
          <DialogOverlay className={isMobile ? '!bg-white/100' : 'bg-black/30'} />
          <Content
            {...(isMobile ? ({}) : {
              hideOverlay: true,
              hideCloseIcon: true
            })}
            side="top"
            className="p-0 max-w-[743px] w-full border-0 rounded-radius-4xl md:!rounded-none md:!max-w-full md:!w-full md:h-full"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSubmit(onSubmit)();
              }
            }}
          >
            <div
              className="p-[3px] md:h-full md:p-0"
              style={isMobile ? undefined : {
                backgroundImage: 'linear-gradient(90deg, #A9C2FE 0%, #FCE9F7 100%)'
              }}
            >
              <div
                className='bg-white p-8 rounded-radius-4xl gap-8 flex flex-col md:p-4 md:h-full md:rounded-none'
                tabIndex={1}>
                <form className="gap-8 flex flex-col md:gap-6" onSubmit={handleSubmit(onSubmit)}>
                  <Controller
                    control={control}
                    name="prompt"
                    render={({ field }) => (
                      <Input
                        autoFocus
                        className="text-gray-950 placeholder:text-gray-300 sm:w-full"
                        containerClassName="!border-0"
                        leftIcon={<SearchIcon className="text-gray-300" />}
                        rightIcon={
                          <Button className="p-0 h-8 w-8" variant="primaryBrand">
                            <MagicSearchIcon className="text-white" />
                          </Button>
                        }
                        placeholder={`Enter your ${isDiscoverPage ? 'spaces' : 'magic'} search`}
                        {...field}
                      />
                    )}
                  />
                  <div className="flex flex-wrap gap-[14px]">
                    {suggestions?.map((item, index) => (
                      <div
                        key={index}
                        className={`${searchWatch === item.request ? 'bg-brand-100 text-brand-700' : 'bg-gray-50 text-gray-500'} flex gap-sm items-center rounded-full px-xl py-sm text-small hover:bg-gray-100 cursor-pointer`}
                        onClick={() => handleSelectSuggestion(item.request)}
                      >
                        {item.request}
                      </div>
                    ))}
                  </div>
                </form>
              </div>
            </div>
          </Content>
        </DialogPortal>
      </DialogRoot>
    </>
  );
};
